import { useCallback, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";

type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

type PendingConfirm = {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  resolve: (value: boolean) => void;
};

export function useConfirmDialog() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback((options?: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({
        title: options?.title || "Confirm Delete",
        message:
          options?.message || "Are you sure you want to delete this record?",
        confirmText: options?.confirmText || "Delete",
        cancelText: options?.cancelText || "Cancel",
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    setPending((curr) => {
      if (curr) curr.resolve(false);
      return null;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setPending((curr) => {
      if (curr) curr.resolve(true);
      return null;
    });
  }, []);

  const ConfirmDialog = useMemo(
    () =>
      function ConfirmDialogComponent() {
        return (
          <Modal
            centered
            show={Boolean(pending)}
            onHide={handleClose}
            className="tv-confirm-modal"
          >
            <Modal.Header>
              <Modal.Title>{pending?.title}</Modal.Title>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={handleClose}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </Modal.Header>
            <Modal.Body>
              <p className="tv-confirm-message mb-0">{pending?.message}</p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                className="tv-confirm-cancel-btn"
                onClick={handleClose}
              >
                {pending?.cancelText}
              </Button>
              <Button
                variant="danger"
                className="tv-confirm-delete-btn"
                onClick={handleConfirm}
              >
                {pending?.confirmText}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      },
    [pending, handleClose, handleConfirm],
  );

  return { confirm, ConfirmDialog };
}

